// Classic non-money unitat view (Caps / plain Unitat role): saldo panel,
// add-despesa toggle, Cap filter, month-grouped timeline via EntryItem.

import { useFactures } from './useFacturesContext';
import { FACTURES_UNITATS } from '../../constants/roles';
import {
	fmtEUR,
	monthLabel,
	fmtEntryAmount,
	type IEntry,
} from './facturesTypes';
import { DespesaForm } from './DespesaForm';
import { EntryItem } from './EntryItem';
import {
	BalancePanel,
	Row,
	BigBalance,
	EntriesList,
	MonthHeader,
	NoteText,
	OwedItem,
	OwedList,
	SubTitle,
	YearSelect,
} from './Factures.styles';
import { Button, Label } from '../Shared/FormStyles';

export const UnitatDetail = () => {
	const fac = useFactures();
	const { detail } = fac;
	if (!detail) return null;

	const groups = (() => {
		let acc = 0;
		const map = new Map<
			string,
			{ label: string; rows: { e: IEntry; amount: number; saldo: number }[] }
		>();
		const shown = detail.entries.filter(
			(e) =>
				fac.capFilter === 'tots' ||
				(e.capName ? e.capName.toLowerCase().replace(/\s+/g, ' ') : '') ===
					fac.capFilter
		);
		if (fac.capFilter !== 'tots') {
			// recalc saldo considering ONLY the shown rows' total offset
			const shownTotal = shown.reduce((a, e) => a + fmtEntryAmount(e), 0);
			acc = detail.balanceCents - shownTotal;
		}
		for (const e of shown) {
			acc += fmtEntryAmount(e);
			const d =
				e.type === 'mensual'
					? `${e.month}-01`
					: (e.expenseDate ?? e.createdAt ?? '').slice(0, 10);
			const monthKey = (d ?? '').slice(0, 7);
			if (!map.has(monthKey)) {
				map.set(monthKey, { label: monthLabel(monthKey), rows: [] });
			}
			map.get(monthKey)!.rows.push({ e, amount: fmtEntryAmount(e), saldo: acc });
		}
		return [...map.values()].reverse();
	})();

	return (
		<>
			{!fac.ownUnitat && (
				<SubTitle>
					{FACTURES_UNITATS.find((u) => u.id === fac.selected)?.nom}{' — '}
					{fac.year}
				</SubTitle>
			)}
			<BalancePanel>
				<BigBalance $negative={detail.balanceCents < 0}>
					{fmtEUR(detail.balanceCents)}
				</BigBalance>
				<div>
					<NoteText>Saldo disponible de la unitat</NoteText>
					{detail.owed.filter((o) => o.owedCents !== 0).length > 0 && (
						<OwedList>
							{detail.owed
								.filter((o) => o.owedCents !== 0)
								.map((o) => (
									<OwedItem key={o.capName}>
										<span>{o.capName}</span>
										<span
											style={{
												color: o.owedCents < 0 ? '#c0392b' : '#333',
											}}
										>
											{o.owedCents > 0
												? `li devem ${fmtEUR(o.owedCents)}`
												: `ens deu ${fmtEUR(-o.owedCents)}`}
										</span>
									</OwedItem>
								))}
						</OwedList>
					)}
				</div>
			</BalancePanel>

			<Row>
				<Button onClick={() => fac.setShowForm((v) => !v)}>
					{fac.showForm ? 'Cancel·la' : 'Afegeix despesa'}
				</Button>
				{/* Cap filter = only meaningful for the non-money timeline */}
				<Label style={{ marginLeft: 'auto' }}>Cap:</Label>
				<YearSelect value={fac.capFilter} onChange={(x) => fac.setCapFilter(x.target.value)}>
					<option value="tots">Tots</option>
					{[
						...new Set(detail.entries.map((x) => x.capName).filter(Boolean)),
					].map((nm) => (
						<option
							key={nm as string}
							value={(nm as string).toLowerCase().replace(/\s+/g, ' ')}
						>
							{nm as string}
						</option>
					))}
				</YearSelect>
			</Row>

			{fac.showForm && <DespesaForm />}

			<EntriesList>
				{groups.map((gr) => (
					<div key={gr.label}>
						<MonthHeader>{gr.label}</MonthHeader>
						{gr.rows.map(({ e, saldo }) => (
							<EntryItem key={e._id} entry={e} saldo={saldo} />
						))}
					</div>
				))}
			</EntriesList>
			{detail.entries.length === 0 && (
				<NoteText>Sense apunts per a aquesta unitat i curs.</NoteText>
			)}
		</>
	);
};

